import { PlayerMatchDetail } from '@/pages/PlayerProfile/PlayerProfile';
import { Typography } from '@dreamteam-monorepo/ui';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { parseISO, isValid } from 'date-fns';
import { formatUkTime, formatUkDateShort } from '@/lib/ukTime';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

export interface PlayerProfileResultFixture {
  matchDetail: PlayerMatchDetail;
}

function formatMatchDateShort(kickoffAt?: string): string {
  if (!kickoffAt) return '';
  const date = parseISO(kickoffAt);
  if (!isValid(date)) return '';
  return formatUkDateShort(kickoffAt);
}

function formatMatchTime(kickoffAt?: string): string {
  if (!kickoffAt) return '';
  const date = parseISO(kickoffAt);
  if (!isValid(date)) return '';
  return formatUkTime(kickoffAt);
}

export interface PlayerProfileResultFixture {
  matchDetail: PlayerMatchDetail;
}

export const PlayerProfilePageFixture: FC<PlayerProfileResultFixture> = ({ matchDetail }) => {
  const navigate = useNavigate();

  const [showDetail, setShowDetail] = useState(false);

  const hasScore =
    matchDetail.leftTeam.score !== undefined && matchDetail.rightTeam.score !== undefined;

  return (
    <div className="flex flex-col gap-3 rounded-xl p-3 shadow-[0px_4px_24px_0px_rgba(34,37,38,0.2)]">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <div className="flex flex-row items-center justify-center gap-1 rounded-[4px] px-2 py-1 bg-[--page-header-bg-grey]">
            {matchDetail.competitionIcon ? matchDetail.competitionIcon : <div />}
            <Typography className="text-[10px] leading-[10px] lg:text-[12px] lg:leading-[16px]">
              {matchDetail.competitionLabel}
            </Typography>
          </div>
          <Typography className="hidden lg:flex text-[12px] leading-[16px]">
            {formatMatchDateShort(matchDetail.kickoffAt)}&nbsp;&nbsp;|&nbsp;&nbsp;
            {matchDetail.venue}
          </Typography>
        </div>
        {hasScore ? (
          <div className="flex gap-1 lg:gap-2 items-center">
            <Typography className="text-[10px] leading-[10px] lg:text-[12px] lg:leading-[16px]">
              {matchDetail.mdLabel} Total:
            </Typography>
            <Typography className="rounded-[4px] px-1 lg:px-2 py-[1px] lg:py-[2px] bg-[#0072EE] text-[12px] lg:text-[14px] leading-[18px] font-bold text-white">
              {matchDetail.mdPoints} PTS
            </Typography>
            {showDetail ? (
              <>
                <ChevronUp
                  className="size-4 lg:size-6 cursor-pointer"
                  onClick={() => {
                    trackFantasyEvent('playerProfileMatchLink', {
                      link_name: 'Collapse details',
                    });
                    setShowDetail(false);
                  }}
                />
              </>
            ) : (
              <>
                <ChevronDown
                  className="size-4 lg:size-6 cursor-pointer"
                  onClick={() => {
                    trackFantasyEvent('playerProfileMatchLink', {
                      link_name: 'Expand details',
                    });
                    setShowDetail(true);
                  }}
                />
              </>
            )}
          </div>
        ) : (
          matchDetail.matchDetailAvailable === true && (
            <Typography
              className="text-[14px] leading-[20px] font-bold text-[#0072EE] cursor-pointer"
              onClick={() => {
                trackFantasyEvent('playerProfileMatchLink', { link_name: 'Match Details' });
                navigate(matchDetail.matchId ? `/match/${matchDetail.matchId}` : '/');
              }}
            >
              Match Details
            </Typography>
          )
        )}
      </div>
      <div className="w-full  bg-[--page-header-bg-grey] rounded-[4px] px-2 py-4 flex flex-col gap-3 items-center justify-center">
        <div className="w-full flex gap-2 items-center justify-center">
          <div className="flex min-w-0 flex-1 items-center justify-end gap-[8px]">
            <Typography variant="paragraph010" className="truncate font-bold text-[12px]">
              {matchDetail.leftTeam.name}
            </Typography>
            <div className="shrink-0 size-8">{matchDetail.leftTeam.logo}</div>
          </div>

          {/* Centre badge */}
          {hasScore ? (
            /* Score + status pill */
            <div className="shrink-0 flex flex-col items-stretch">
              <div className="bg-[var(--match-score-bg,#ffffff)] rounded-t-[4px] bg-white px-[8px] py-[2px] flex items-center justify-center">
                <Typography
                  as="span"
                  variant="subheadline020"
                  className="text-[var(--match-score-text,#222526)] whitespace-nowrap"
                >
                  {matchDetail.leftTeam.score} - {matchDetail.rightTeam.score}
                </Typography>
              </div>
              {matchDetail.status && (
                <div
                  className={twMerge(
                    'rounded-b-[4px] bg-[#201D4A] px-[8px] py-[2px]',
                    'flex items-center justify-center',
                    matchDetail.statusVariant === 'live'
                      ? 'bg-[var(--match-status-live-bg,#8ce28b)]'
                      : 'bg-[var(--match-status-ft-bg,#201d4a)]'
                  )}
                >
                  <Typography
                    as="span"
                    variant="paragraph000"
                    className={twMerge(
                      'whitespace-nowrap font-bold  text-[10px]',
                      matchDetail.statusVariant === 'live'
                        ? 'text-[var(--match-status-live-text,#222526)]'
                        : 'text-[var(--match-status-ft-text,#ffffff)]'
                    )}
                  >
                    {matchDetail.status}
                  </Typography>
                </div>
              )}
            </div>
          ) : matchDetail.kickoffAt ? (
            /* Kick-off time pill */
            <div className="shrink-0 bg-[var(--match-kickoff-bg,#ffffff)] px-[--padding-2xs] py-[--padding-3xs] rounded-[var(--corner-xs,4px)] flex items-center justify-center">
              <Typography
                as="span"
                variant="subheadline010"
                className="text-[12px] leading-[16px] text-[var(--match-kickoff-text,#222526)] whitespace-nowrap"
              >
                {formatMatchTime(matchDetail.kickoffAt)}
              </Typography>
            </div>
          ) : null}

          {/* Right team: logo → name */}
          <div className="flex min-w-0 flex-1 items-center justify-start gap-[8px]">
            <div className="shrink-0 size-8">{matchDetail.rightTeam.logo}</div>
            <Typography variant="paragraph010" className="truncate font-bold text-[12px]">
              {matchDetail.rightTeam.name}
            </Typography>
          </div>
        </div>
        {!hasScore && matchDetail.kickoffAt && (
          <Typography className="flex lg:hidden text-[10px] leading-[10px] mx-auto">
            {formatMatchDateShort(matchDetail.kickoffAt)}&nbsp;&nbsp;|&nbsp;&nbsp;
            {matchDetail.venue}
          </Typography>
        )}
      </div>
      {showDetail && hasScore ? (
        <div>
          <div className="flex items-center gap-[16px] border-b-2 border-[--color-neutral-100] pb-[12px]">
            <Typography
              variant="paragraph000"
              className="flex-1 text-[--color-neutral-600] text-[10px] leading-[10px]"
            >
              STAT
            </Typography>
            <div className="flex gap-2">
              <Typography
                variant="paragraph000"
                className="w-[40px] text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
              >
                TOTAL
              </Typography>
              <Typography
                variant="paragraph000"
                className="w-[40px] text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
              >
                POINTS
              </Typography>
            </div>
          </div>
          {matchDetail.stats.length === 0 ? (
            <div className="flex items-center justify-center py-[16px]">
              <Typography
                variant="paragraph000"
                className="text-[--color-neutral-600] text-[12px] leading-[16px]"
              >
                No stats recorded for this match
              </Typography>
            </div>
          ) : (
            matchDetail.stats.map((statRow, index) => (
              <div
                key={index}
                className="flex items-center gap-[16px] border-b-2 border-[--color-neutral-100] py-[12px]"
              >
                <Typography
                  variant="paragraph000"
                  className="flex-1 text-[--color-neutral-900] text-[12px] leading-[16px] font-bold"
                >
                  {statRow.label}
                </Typography>
                <div className="flex gap-2">
                  <Typography
                    variant="paragraph000"
                    className="w-[40px] text-center text-[--color-neutral-900] text-[14px] leading-[20px] font-bold"
                  >
                    {statRow.total}
                  </Typography>
                  <Typography
                    variant="paragraph000"
                    className="w-[40px] text-center text-[14px] leading-[20px] font-bold text-[#0072EE]"
                  >
                    {statRow.points}
                  </Typography>
                </div>
              </div>
            ))
          )}
          {matchDetail.matchDetailAvailable === true && (
            <Typography
              className="text-[14px] leading-[20px] font-bold text-[#0072EE] text-center cursor-pointer py-[12px]"
              onClick={() => {
                trackFantasyEvent('playerProfileMatchLink', { link_name: 'View Match Details' });
                navigate(matchDetail.matchId ? `/match/${matchDetail.matchId}` : '/');
              }}
            >
              View Match Details
            </Typography>
          )}
        </div>
      ) : null}
    </div>
  );
};
